import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  AlertTriangle,
  LayoutGrid,
  List,
  Search,
  Table2,
  Users,
} from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";

import AssignedAccountsModal from "./AssignedAccountsModal";
import AssignmentHistoryModal from "./AssignmentHistoryModal";
import type { AssignmentHistoryTarget } from "./AssignmentHistoryModal";
import ReassignModal, { isAssignmentReassignable } from "./ReassignModal";
import { useTeamViewsPrivilegedAccess } from "./teamViewsAccess";
import TeamViewsAccountBoard from "./TeamViewsAccountBoard";
import TeamViewsAccountCell from "./TeamViewsAccountCell";
import TeamViewsEmptyState from "./TeamViewsEmptyState";
import {
  ACCOUNT_COLUMN_LABELS,
  ACCOUNT_FILTER_DEFS,
  DEFAULT_ACCOUNT_COLUMNS,
  accountFilterCoreColumns,
  accountMatchesFilter,
  formatAccountColumnCell,
  getAccountColumnPeople,
  groupPeopleByRole,
  isCardPerson,
  personRoleLabel,
  visibleAccountColumns,
} from "./teamViewsHelpers";
import type { AccountFilterId } from "./teamViewsHelpers";
import TeamViewsPersonCard from "./TeamViewsPersonCard";
import TeamViewsPersonRow from "./TeamViewsPersonRow";
import TeamViewsRoleGapsModal from "./TeamViewsRoleGapsModal";
import TeamViewsRoleSections from "./TeamViewsRoleSections";
import TabsComponent from "../../../Components/Tab";
import { useToast } from "../../../Components/Toast";
import ToggleSwitch from "../../../Components/ToggleSwitch";
import { useAppDispatch, useAppSelector } from "../../../Store/Hooks";
import {
  applyTeamReassign,
  fetchDepartedRows,
  fetchTeamViewsRoleGaps,
  fetchTeamViewsRoster,
  previewTeamReassign,
  type TeamViewsAccountRow,
  type TeamViewsPerson,
} from "../../../Store/Reducers/TeamViewsSlice";
import { RELATIONSHIP_ROUTE_PATH } from "../../../Utils/Constants/RoleConstants";
import {
  getFromLocalStorage,
  STORAGE_KEYS,
} from "../../../Utils/Helper/LocalStorage";
import { orgPath } from "../../../Utils/Helper/orgPath";
import RelationshipAccountsLoader from "../RelationshipAccountsLoader";
import { resolveLoggedInUserId } from "../stakeholderPortfolioScopeUtils";

import "../account-relationships/relationship-prototype-skin.css";

type ViewMode = "by-person" | "by-account";
type PersonLayout = "cards" | "list";
type AccountLayout = "cards" | "list";

const VIEW_TABS = [
  { id: "by-person", label: "By Person", icon: <Users size={14} /> },
  { id: "by-account", label: "By Account", icon: <Table2 size={14} /> },
];

/** Client-approved Team Views V3 shell (no V1–V6 switcher). */
const CARD_VARIANT = "v3" as const;

const TeamViewsShell: React.FC = () => {
  const cardVariant = CARD_VARIANT;
  const canManageTeamCoverage = useTeamViewsPrivilegedAccess();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const toast = useToast();
  const {
    people,
    accounts,
    accountColumns,
    showPortfolioHighlight,
    viewerAccountIds,
    roleGaps,
    rosterLoading,
    gapsLoading,
  } = useAppSelector(state => state.teamViews);
  const [view, setView] = useState<ViewMode>("by-person");
  const [personLayout, setPersonLayout] = useState<PersonLayout>("cards");
  const [accountLayout, setAccountLayout] = useState<AccountLayout>("list");
  const [roleFilter, setRoleFilter] = useState<string>("all");
  const [roleScrollRequestId, setRoleScrollRequestId] = useState(0);
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState(() => searchParams.get("q") || "");
  const [showMyTeam, setShowMyTeam] = useState(false);
  const [showAllRoles, setShowAllRoles] = useState(false);
  const [accountFilter, setAccountFilter] = useState<AccountFilterId>("all");
  const [accountsPerson, setAccountsPerson] = useState<TeamViewsPerson | null>(
    null
  );
  const [accountsModalOpen, setAccountsModalOpen] = useState(false);
  const [reassignPerson, setReassignPerson] = useState<TeamViewsPerson | null>(
    null
  );
  const [reassignFocusAccountId, setReassignFocusAccountId] = useState<
    string | null
  >(null);
  const [previewData, setPreviewData] = useState<any>(null);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [historyTarget, setHistoryTarget] =
    useState<AssignmentHistoryTarget | null>(null);
  const [roleGapsModalOpen, setRoleGapsModalOpen] = useState(false);
  const contentScrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    dispatch(fetchTeamViewsRoster());
    dispatch(fetchTeamViewsRoleGaps());
  }, [dispatch]);

  // Drop Show my team if the role/portfolio gate no longer allows the toggle.
  useEffect(() => {
    if (!showPortfolioHighlight || !viewerAccountIds.length) {
      setShowMyTeam(false);
    }
  }, [showPortfolioHighlight, viewerAccountIds.length]);

  const filteredPeople = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return people;
    return people.filter(
      (p: TeamViewsPerson) =>
        (p.full_name || "").toLowerCase().includes(q) ||
        personRoleLabel(p).toLowerCase().includes(q) ||
        (p.role_name || "").toLowerCase().includes(q) ||
        p.assignments.some((a: TeamViewsPerson["assignments"][number]) =>
          (a.account_name || "").toLowerCase().includes(q)
        )
    );
  }, [people, search]);

  const personPeople = useMemo(
    () => filteredPeople.filter(isCardPerson),
    [filteredPeople]
  );

  const loggedInUserId = resolveLoggedInUserId(
    getFromLocalStorage(STORAGE_KEYS.LOGGED_IN_USER)
  );

  // "Show my team" keeps only people sharing at least one account with the viewer,
  // and drops the logged-in user so they are not duplicated under every account.
  const teamScopedPeople = useMemo(() => {
    if (!showMyTeam) return personPeople;
    return personPeople.filter(
      p =>
        Boolean(p.overlaps_viewer_portfolio) &&
        (!loggedInUserId || String(p.user_id) !== loggedInUserId)
    );
  }, [personPeople, showMyTeam, loggedInUserId]);

  // Chips come from the whole roster so the set stays stable; counts follow the search.
  const roleChips = useMemo(() => {
    const counts = new Map<string, number>();
    for (const person of teamScopedPeople) {
      const label = personRoleLabel(person) || "Other";
      counts.set(label, (counts.get(label) ?? 0) + 1);
    }
    const roster = people
      .filter(isCardPerson)
      .filter(
        p =>
          !showMyTeam ||
          (Boolean(p.overlaps_viewer_portfolio) &&
            (!loggedInUserId || String(p.user_id) !== loggedInUserId))
      );
    const roleLabels = groupPeopleByRole(roster).map(group => group.roleLabel);
    return [
      { id: "all", label: "All", count: teamScopedPeople.length },
      ...roleLabels.map(label => ({
        id: label,
        label,
        count: counts.get(label) ?? 0,
      })),
    ];
  }, [people, teamScopedPeople, showMyTeam, loggedInUserId]);

  // A role can disappear when "Show my team" turns on — don't strand the view empty.
  useEffect(() => {
    if (roleFilter === "all") return;
    if (!roleChips.some(chip => chip.id === roleFilter)) setRoleFilter("all");
  }, [roleChips, roleFilter]);

  const visiblePeople = useMemo(() => {
    if (roleFilter === "all") return teamScopedPeople;
    return teamScopedPeople.filter(
      p => (personRoleLabel(p) || "Other") === roleFilter
    );
  }, [teamScopedPeople, roleFilter]);

  /** Role chip selected: keep full roster (incl. Show my team), jump to role section. */
  const magneticRoleMode = roleFilter !== "all";

  const azPeople = useMemo(
    () =>
      [...teamScopedPeople].sort((a, b) =>
        (a.full_name || "").localeCompare(b.full_name || "")
      ),
    [teamScopedPeople]
  );

  const flatCardPeople = roleFilter === "all" ? azPeople : visiblePeople;

  // List "All" = A–Z; role chip = magnetic sections (handled separately).
  const listPeople = useMemo(
    () =>
      roleFilter === "all"
        ? azPeople
        : groupPeopleByRole(visiblePeople).flatMap(group => group.members),
    [azPeople, roleFilter, visiblePeople]
  );

  // With "Show my team" on, cards are grouped per portfolio account so it's obvious
  // who covers what. Someone on two of your accounts appears under both.
  const accountGroups = useMemo(() => {
    if (!showMyTeam) return [];
    const portfolio = new Set(viewerAccountIds);
    const groups = new Map<
      string,
      { accountId: string; accountName: string; members: TeamViewsPerson[] }
    >();
    for (const person of visiblePeople) {
      for (const assignment of person.assignments) {
        const accountId = String(assignment.account_id);
        if (!portfolio.has(accountId)) continue;
        let group = groups.get(accountId);
        if (!group) {
          group = {
            accountId,
            accountName: assignment.account_name || "Unnamed account",
            members: [],
          };
          groups.set(accountId, group);
        }
        group.members.push(person);
      }
    }
    return Array.from(groups.values()).sort((a, b) =>
      a.accountName.localeCompare(b.accountName)
    );
  }, [showMyTeam, viewerAccountIds, visiblePeople]);

  // "Show my team" narrows By Account to the viewer's own accounts; the cells still
  // list everyone covering them, so coverage stays readable.
  const scopedAccounts = useMemo(() => {
    if (!showMyTeam) return accounts;
    const portfolio = new Set(viewerAccountIds);
    return accounts.filter((a: TeamViewsAccountRow) =>
      portfolio.has(String(a.account_id))
    );
  }, [accounts, showMyTeam, viewerAccountIds]);

  // With "Show my team" on, hide the logged-in user from account cells.
  const showMyTeamExcludeUserId = showMyTeam ? loggedInUserId || null : null;

  const isYouPerson = useCallback(
    (person: TeamViewsPerson) =>
      !showMyTeam &&
      Boolean(loggedInUserId) &&
      String(person.user_id) === loggedInUserId,
    [showMyTeam, loggedInUserId]
  );

  const filteredAccounts = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return scopedAccounts;
    return scopedAccounts.filter((a: TeamViewsAccountRow) => {
      if ((a.account_name || "").toLowerCase().includes(q)) return true;
      const columns = accountColumns.length
        ? accountColumns
        : DEFAULT_ACCOUNT_COLUMNS;
      return columns.some(col => {
        const value = formatAccountColumnCell(a, col);
        return value !== "—" && value.toLowerCase().includes(q);
      });
    });
  }, [scopedAccounts, search, accountColumns]);

  const tableColumns = accountColumns.length
    ? accountColumns
    : DEFAULT_ACCOUNT_COLUMNS;

  const accountCoreColumns = useMemo(
    () => accountFilterCoreColumns(tableColumns),
    [tableColumns]
  );

  const accountChips = useMemo(() => {
    const counts = new Map<AccountFilterId, number>();
    for (const def of ACCOUNT_FILTER_DEFS) {
      counts.set(
        def.id,
        filteredAccounts.filter(row =>
          accountMatchesFilter(row, def.id, accountCoreColumns)
        ).length
      );
    }
    return ACCOUNT_FILTER_DEFS.filter(def => {
      const count = counts.get(def.id) ?? 0;
      if (def.hideWhenEmpty && count === 0) return false;
      return true;
    }).map(def => ({
      id: def.id,
      label: def.label,
      count: counts.get(def.id) ?? 0,
    }));
  }, [filteredAccounts, accountCoreColumns]);

  // Chip may disappear when search/scope changes — reset to All.
  useEffect(() => {
    if (accountFilter === "all") return;
    if (!accountChips.some(chip => chip.id === accountFilter)) {
      setAccountFilter("all");
    }
  }, [accountChips, accountFilter]);

  const displayAccounts = useMemo(
    () =>
      filteredAccounts.filter(row =>
        accountMatchesFilter(row, accountFilter, accountCoreColumns)
      ),
    [filteredAccounts, accountFilter, accountCoreColumns]
  );

  const displayColumns = useMemo(
    () => visibleAccountColumns(tableColumns, filteredAccounts, showAllRoles),
    [tableColumns, filteredAccounts, showAllRoles]
  );

  const peopleById = useMemo(() => {
    const map = new Map<string, TeamViewsPerson>();
    for (const person of people) {
      map.set(String(person.user_id), person);
    }
    return map;
  }, [people]);

  const resolvePerson = useCallback(
    (userId?: string | null) =>
      userId ? peopleById.get(String(userId)) : undefined,
    [peopleById]
  );

  const openReassign = useCallback((person: TeamViewsPerson) => {
    const hasEligible = person.assignments.some(isAssignmentReassignable);
    if (!hasEligible) return;
    setReassignFocusAccountId(null);
    setReassignPerson(person);
    setPreviewData(null);
    setReassignOpen(true);
  }, []);

  const openReassignForAccount = useCallback(
    (person: TeamViewsPerson, accountId: string) => {
      const assignment = person.assignments.find(
        a => String(a.account_id) === String(accountId)
      );
      if (!assignment || !isAssignmentReassignable(assignment)) return;
      setAccountsModalOpen(false);
      setAccountsPerson(null);
      setReassignFocusAccountId(String(accountId));
      setReassignPerson(person);
      setPreviewData(null);
      setReassignOpen(true);
    },
    []
  );

  const openHistoryForAccount = useCallback(
    (person: TeamViewsPerson, accountId: string) => {
      const assignment = person.assignments.find(
        a => String(a.account_id) === String(accountId)
      );
      setHistoryTarget({
        personUserId: String(person.user_id),
        personName: person.full_name || "Unknown",
        accountId: String(accountId),
        accountName: assignment?.account_name || "Account",
      });
    },
    []
  );

  const closeReassign = useCallback(() => {
    setReassignOpen(false);
    setReassignPerson(null);
    setReassignFocusAccountId(null);
    setPreviewData(null);
  }, []);

  const openAssignedAccounts = useCallback((person: TeamViewsPerson) => {
    setAccountsPerson(person);
    setAccountsModalOpen(true);
  }, []);

  const openPersonFromChip = useCallback(
    (userId?: string | null) => {
      if (!userId) return;
      const person = peopleById.get(String(userId));
      if (person) openAssignedAccounts(person);
    },
    [peopleById, openAssignedAccounts]
  );

  const openAccountRelationships = useCallback(
    (accountId?: string | null) => {
      const id = String(accountId || "").trim();
      if (!id) return;
      const params = new URLSearchParams({
        tab: "account-relationships",
        accountId: id,
      });
      navigate(`${orgPath(RELATIONSHIP_ROUTE_PATH)}?${params.toString()}`);
    },
    [navigate]
  );

  const handlePreview = useCallback(
    async (
      selections: { account_id: string; successor_user_id?: string }[]
    ) => {
      if (!reassignPerson) return;
      const data = await dispatch(
        previewTeamReassign({
          person_user_id: reassignPerson.user_id,
          accounts: selections,
        })
      ).unwrap();
      setPreviewData(data);
    },
    [dispatch, reassignPerson]
  );

  const handleConfirmReassign = useCallback(
    async (
      selections: { account_id: string; successor_user_id?: string }[]
    ) => {
      if (!reassignPerson) return;
      try {
        await dispatch(
          applyTeamReassign({
            person_user_id: reassignPerson.user_id,
            accounts: selections,
          })
        ).unwrap();
        toast.success("Reassignment applied");
        setReassignOpen(false);
        setReassignPerson(null);
        setReassignFocusAccountId(null);
        setPreviewData(null);
        dispatch(fetchTeamViewsRoster());
        dispatch(fetchTeamViewsRoleGaps());
        selections.forEach(sel => {
          dispatch(fetchDepartedRows(sel.account_id));
        });
      } catch (err: any) {
        toast.error(err?.message || "Reassign failed");
      }
    },
    [dispatch, reassignPerson, toast]
  );

  const clearSearch = useCallback(() => setSearch(""), []);

  const personEmptyState = (
    <TeamViewsEmptyState
      scope="people"
      searchQuery={search}
      title={
        search.trim()
          ? undefined
          : showMyTeam
            ? "No one on your team"
            : undefined
      }
      description={
        search.trim()
          ? undefined
          : showMyTeam
            ? "No one is assigned to your portfolio accounts."
            : undefined
      }
      onClearSearch={clearSearch}
    />
  );

  const accountEmptyState = (
    <TeamViewsEmptyState
      scope="accounts"
      searchQuery={search}
      title={
        search.trim()
          ? undefined
          : accountFilter !== "all"
            ? "No accounts match this filter"
            : showMyTeam
              ? "No portfolio accounts"
              : undefined
      }
      description={
        search.trim()
          ? undefined
          : accountFilter !== "all"
            ? "Try another coverage filter or clear search."
            : showMyTeam
              ? "You have no portfolio accounts."
              : undefined
      }
      onClearSearch={clearSearch}
    />
  );

  const cardsEmpty = magneticRoleMode
    ? !teamScopedPeople.length
    : showMyTeam
      ? !accountGroups.length
      : !flatCardPeople.length;

  // By Account list table: scroll inside the wrap with a sticky header (not the page).
  const accountListTableMode =
    view === "by-account" && accountLayout === "list";

  return (
    <div className="rl-prototype-skin team-views-shell flex min-h-0 flex-1 flex-col overflow-hidden">
      {!gapsLoading && roleGaps.length > 0 ? (
        <div className="tv-role-gaps-strip tv-role-gaps-strip--summary mb-3 shrink-0">
          <div className="tv-role-gaps-hdr">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />
            ROLE GAPS ({roleGaps.length})
          </div>
          <button
            type="button"
            className="tv-role-gap-explore"
            onClick={() => setRoleGapsModalOpen(true)}
          >
            Explore
          </button>
        </div>
      ) : null}

      <TeamViewsRoleGapsModal
        open={roleGapsModalOpen}
        gaps={roleGaps}
        onClose={() => setRoleGapsModalOpen(false)}
      />

      <div className="tv-shell-card flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="tv-shell-primary-hdr shrink-0 flex flex-wrap items-center gap-3 border-b border-gray-200 bg-gray-50 px-4 py-3">
          <TabsComponent
            tabs={VIEW_TABS}
            activeTab={view}
            onTabChange={(id: string) => setView(id as ViewMode)}
            variant="tab-segmented"
            className="mb-0"
          />
          <div className="relative w-full min-w-[280px] sm:ml-auto sm:w-[320px] sm:max-w-none">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
              aria-hidden
            />
            <input
              type="search"
              name="tv-search"
              autoComplete="off"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search people or accounts"
              aria-label="Search people or accounts"
              className="h-10 w-full rounded-md border border-gray-200 bg-gray-50/50 pl-10 pr-4 text-sm text-gray-900 placeholder:text-gray-400 focus:border-gray-300 focus:bg-white focus:outline-none focus:ring-1 focus:ring-gray-200"
            />
          </div>
          {/* Same gate as Your portfolio: role-eligible + has linked accounts. */}
          {showPortfolioHighlight && viewerAccountIds.length ? (
            <ToggleSwitch
              id="tv-show-my-team"
              className="tv-show-my-team"
              label="Show my team"
              checked={showMyTeam}
              onChange={setShowMyTeam}
            />
          ) : null}
        </div>

        {view === "by-person" ? (
          <div className="tv-shell-secondary-hdr shrink-0 flex flex-wrap items-center gap-2 border-b border-gray-100 bg-white px-4 py-3">
            <div
              className="flex flex-wrap items-center gap-2"
              role="group"
              aria-label="Browse people by role"
            >
              {roleChips.map(chip => (
                <button
                  key={chip.id}
                  type="button"
                  className={`tv-role-chip${roleFilter === chip.id ? " is-active" : ""}`}
                  aria-pressed={roleFilter === chip.id}
                  onClick={() => {
                    setRoleFilter(chip.id);
                    if (chip.id !== "all") {
                      setRoleScrollRequestId(id => id + 1);
                    } else if (contentScrollRef.current) {
                      contentScrollRef.current.scrollTo({
                        top: 0,
                        behavior: "smooth",
                      });
                    }
                  }}
                >
                  {chip.label} · {chip.count}
                </button>
              ))}
            </div>
            <div
              className="tv-person-layout-toggle ml-auto inline-flex items-center gap-2 rounded-md border border-gray-200 bg-white p-2"
              role="tablist"
              aria-label="Person layout"
            >
              <button
                type="button"
                role="tab"
                className={`tv-person-layout-btn inline-flex items-center justify-center rounded p-2 text-xs font-medium${personLayout === "cards" ? " is-active bg-purple-100 text-purple-700" : " text-gray-500 hover:bg-gray-50"}`}
                aria-label="Card view"
                aria-selected={personLayout === "cards"}
                title="Card view"
                onClick={() => setPersonLayout("cards")}
              >
                <LayoutGrid size={14} aria-hidden />
              </button>
              <button
                type="button"
                role="tab"
                className={`tv-person-layout-btn inline-flex items-center justify-center rounded p-2 text-xs font-medium${personLayout === "list" ? " is-active bg-purple-100 text-purple-700" : " text-gray-500 hover:bg-gray-50"}`}
                aria-label="List view"
                aria-selected={personLayout === "list"}
                title="List view"
                onClick={() => setPersonLayout("list")}
              >
                <List size={14} aria-hidden />
              </button>
            </div>
          </div>
        ) : (
          <div className="tv-shell-secondary-hdr shrink-0 flex flex-wrap items-center gap-2 border-b border-gray-100 bg-white px-4 py-3">
            <div
              className="flex flex-wrap items-center gap-2"
              role="group"
              aria-label="Filter accounts by coverage"
            >
              {accountChips.map(chip => (
                <button
                  key={chip.id}
                  type="button"
                  className={`tv-role-chip${accountFilter === chip.id ? " is-active" : ""}`}
                  aria-pressed={accountFilter === chip.id}
                  onClick={() => setAccountFilter(chip.id)}
                >
                  {chip.label} · {chip.count}
                </button>
              ))}
            </div>
            <ToggleSwitch
              id="tv-show-all-roles"
              className="tv-show-all-roles ml-auto"
              label="Show all roles"
              checked={showAllRoles}
              onChange={setShowAllRoles}
            />
            <div
              className="tv-person-layout-toggle inline-flex items-center gap-2 rounded-md border border-gray-200 bg-white p-2"
              role="tablist"
              aria-label="Account layout"
            >
              <button
                type="button"
                role="tab"
                className={`tv-person-layout-btn inline-flex items-center justify-center rounded p-2 text-xs font-medium${accountLayout === "list" ? " is-active bg-purple-100 text-purple-700" : " text-gray-500 hover:bg-gray-50"}`}
                aria-label="List view"
                aria-selected={accountLayout === "list"}
                title="List view"
                onClick={() => setAccountLayout("list")}
              >
                <List size={14} aria-hidden />
              </button>
              <button
                type="button"
                role="tab"
                className={`tv-person-layout-btn inline-flex items-center justify-center rounded p-2 text-xs font-medium${accountLayout === "cards" ? " is-active bg-purple-100 text-purple-700" : " text-gray-500 hover:bg-gray-50"}`}
                aria-label="Card view"
                aria-selected={accountLayout === "cards"}
                title="Card view"
                onClick={() => setAccountLayout("cards")}
              >
                <LayoutGrid size={14} aria-hidden />
              </button>
            </div>
          </div>
        )}

        <div
          ref={contentScrollRef}
          className={`tv-shell-scroll min-h-0 flex-1 p-4${
            accountListTableMode
              ? " tv-shell-scroll--account-list overflow-hidden"
              : " overflow-y-auto"
          }`}
        >
          {rosterLoading ? (
            <RelationshipAccountsLoader label="Loading team roster…" />
          ) : null}

          {!rosterLoading &&
          view === "by-person" &&
          personLayout === "cards" ? (
            cardsEmpty ? (
              personEmptyState
            ) : magneticRoleMode ? (
              <TeamViewsRoleSections
                people={teamScopedPeople}
                activeRole={roleFilter}
                scrollRequestId={roleScrollRequestId}
                scrollRootRef={contentScrollRef}
                onActiveRoleChange={setRoleFilter}
                layout="cards"
                cardVariant={cardVariant}
                showPortfolioHighlight={showPortfolioHighlight}
                youUserId={showMyTeam ? null : loggedInUserId || null}
                canManageTeamCoverage={canManageTeamCoverage}
                onSelectPerson={openAssignedAccounts}
              />
            ) : showMyTeam ? (
              <div className="tv-account-groups">
                {accountGroups.map(group => (
                  <section className="tv-account-group" key={group.accountId}>
                    <h4 className="tv-account-group-hdr">
                      {group.accountName}
                      <span className="tv-account-group-count">
                        {group.members.length}
                      </span>
                    </h4>
                    <div className="tv-cards-grid tv-cards-grid--v3">
                      {group.members.map((person: TeamViewsPerson) => (
                        <TeamViewsPersonCard
                          key={`${group.accountId}-${person.user_id}`}
                          person={person}
                          showPortfolioHighlight={showPortfolioHighlight}
                          variant={cardVariant}
                          isYou={isYouPerson(person)}
                          showLastLogin={canManageTeamCoverage}
                          onClick={openAssignedAccounts}
                        />
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            ) : (
              <div className="tv-cards-grid tv-cards-grid--v3">
                {flatCardPeople.map((person: TeamViewsPerson) => (
                  <TeamViewsPersonCard
                    key={person.user_id}
                    person={person}
                    showPortfolioHighlight={showPortfolioHighlight}
                    variant={cardVariant}
                    isYou={isYouPerson(person)}
                    showLastLogin={canManageTeamCoverage}
                    onClick={openAssignedAccounts}
                  />
                ))}
              </div>
            )
          ) : null}

          {!rosterLoading && view === "by-person" && personLayout === "list" ? (
            !(showMyTeam && !magneticRoleMode
              ? accountGroups.length
              : magneticRoleMode
                ? teamScopedPeople.length
                : listPeople.length) ? (
              personEmptyState
            ) : magneticRoleMode ? (
              <TeamViewsRoleSections
                people={teamScopedPeople}
                activeRole={roleFilter}
                scrollRequestId={roleScrollRequestId}
                scrollRootRef={contentScrollRef}
                onActiveRoleChange={setRoleFilter}
                layout="list"
                showPortfolioHighlight={showPortfolioHighlight}
                youUserId={showMyTeam ? null : loggedInUserId || null}
                canManageTeamCoverage={canManageTeamCoverage}
                onReassignPerson={openReassign}
                onReassignAccount={openReassignForAccount}
                onHistoryAccount={openHistoryForAccount}
              />
            ) : showMyTeam ? (
              <div className="tv-account-groups">
                {accountGroups.map(group => (
                  <section className="tv-account-group" key={group.accountId}>
                    <h4 className="tv-account-group-hdr">
                      {group.accountName}
                      <span className="tv-account-group-count">
                        {group.members.length}
                      </span>
                    </h4>
                    <div className="tv-accordion">
                      {groupPeopleByRole(group.members)
                        .flatMap(g => g.members)
                        .map((person: TeamViewsPerson) => (
                          <TeamViewsPersonRow
                            key={`${group.accountId}-${person.user_id}`}
                            person={person}
                            showPortfolioHighlight={showPortfolioHighlight}
                            isYou={isYouPerson(person)}
                            canManageTeamCoverage={canManageTeamCoverage}
                            onReassignPerson={openReassign}
                            onReassignAccount={openReassignForAccount}
                            onHistoryAccount={openHistoryForAccount}
                          />
                        ))}
                    </div>
                  </section>
                ))}
              </div>
            ) : (
              <div className="tv-accordion">
                {listPeople.map((person: TeamViewsPerson) => (
                  <TeamViewsPersonRow
                    key={person.user_id}
                    person={person}
                    showPortfolioHighlight={showPortfolioHighlight}
                    isYou={isYouPerson(person)}
                    canManageTeamCoverage={canManageTeamCoverage}
                    onReassignPerson={openReassign}
                    onReassignAccount={openReassignForAccount}
                    onHistoryAccount={openHistoryForAccount}
                  />
                ))}
              </div>
            )
          ) : null}

          {!rosterLoading && view === "by-account" ? (
            displayAccounts.length ? (
              accountLayout === "cards" ? (
                <TeamViewsAccountBoard
                  accounts={displayAccounts}
                  columns={displayColumns}
                  excludeUserId={showMyTeamExcludeUserId}
                  resolvePerson={resolvePerson}
                  onSelectPerson={openPersonFromChip}
                  onSelectAccount={openAccountRelationships}
                />
              ) : (
                <div className="tv-by-account-wrap tv-by-account-wrap--scroll tv-by-account-wrap--v3 overflow-auto rounded-xl border border-gray-200 bg-white">
                  <table className="tv-by-account-table min-w-full">
                    <thead>
                      <tr className="tv-by-account-hdr-row border-b text-left">
                        <th className="tv-by-account-sticky px-3 py-2.5">
                          ACCOUNT
                        </th>
                        {displayColumns.map(columnKey => {
                          const label =
                            ACCOUNT_COLUMN_LABELS[columnKey] || columnKey;
                          return (
                            <th
                              key={columnKey}
                              className="px-3 py-2.5 whitespace-nowrap"
                              title={label}
                            >
                              {label.toUpperCase()}
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {displayAccounts.map((row: TeamViewsAccountRow) => (
                        <tr
                          key={row.account_id}
                          className="tv-by-account-row border-b border-gray-50 last:border-0"
                        >
                          <td
                            className="tv-by-account-sticky px-3 py-2.5"
                            title={row.account_name || undefined}
                          >
                            <button
                              type="button"
                              className="tv-by-account-acct-name tv-by-account-acct-name--link"
                              onClick={() =>
                                openAccountRelationships(row.account_id)
                              }
                            >
                              {row.account_name}
                            </button>
                          </td>
                          {displayColumns.map(columnKey => (
                            <td
                              key={columnKey}
                              className="px-3 py-2.5 align-top"
                            >
                              <TeamViewsAccountCell
                                people={getAccountColumnPeople(
                                  row,
                                  columnKey,
                                  showMyTeamExcludeUserId
                                )}
                                roleLabel={
                                  ACCOUNT_COLUMN_LABELS[columnKey] || columnKey
                                }
                                variant={cardVariant}
                                resolvePerson={resolvePerson}
                                onSelectPerson={openPersonFromChip}
                              />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            ) : (
              accountEmptyState
            )
          ) : null}
        </div>
      </div>

      <AssignedAccountsModal
        open={accountsModalOpen}
        person={accountsPerson}
        canManageTeamCoverage={canManageTeamCoverage}
        onClose={() => {
          setAccountsModalOpen(false);
          setAccountsPerson(null);
        }}
        onReassignAccount={(person, assignment) =>
          openReassignForAccount(person, assignment.account_id)
        }
      />
      <AssignmentHistoryModal
        open={Boolean(historyTarget)}
        target={historyTarget}
        onClose={() => setHistoryTarget(null)}
      />
      <ReassignModal
        open={reassignOpen}
        person={reassignPerson}
        focusAccountId={reassignFocusAccountId}
        preview={previewData}
        onClose={closeReassign}
        onPreview={handlePreview}
        onConfirm={handleConfirmReassign}
      />
    </div>
  );
};

export default TeamViewsShell;
